// Admin · Pricing
// Per-property nightly rate sheet. Defaults are derived from the room
// registry; the admin can override them in EUR (the page converts to USD
// / MAD client-side). Same UX pattern as `/admin/testimonials`.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Save, RefreshCw, Plus, Trash2, Loader2,
  CheckCircle2, AlertCircle, ArrowUp, ArrowDown,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import { HOTELS } from "@/lib/data";
import { buildDefaultPricingForHotel, BOARD_BASIS_LABELS } from "@/lib/pricingDefaults";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LANGS = [
  { id: "en", label: "EN" },
  { id: "fr", label: "FR" },
  { id: "es", label: "ES" },
];
const blankI18n = () => ({ en: "", fr: "", es: "" });
const blankPriceRow = () => ({
  id: `local-${Math.random().toString(36).slice(2, 10)}`,
  room_id: "",
  room_name: blankI18n(),
  capacity: blankI18n(),
  characteristics: blankI18n(),
  prices: { room_only: null, bb: null, hb: null, fb: null },
});

const I18nField = ({ label, value, onChange, rows = 1, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1.5">{label}</p>
    <div className="space-y-2">
      {LANGS.map((l) => (
        <label key={l.id} className="flex gap-2 items-start">
          <span className="mt-2.5 inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
            {l.label}
          </span>
          {rows === 1 ? (
            <input
              value={value?.[l.id] || ""}
              onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
            />
          ) : (
            <textarea
              value={value?.[l.id] || ""}
              onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
              rows={rows}
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
            />
          )}
        </label>
      ))}
    </div>
  </div>
);

const PricingEditor = ({ slug, hotel }) => {
  const [rows, setRows] = useState([]);
  const [isOverride, setIsOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/pricing/${slug}`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data.rows) && data.rows.length) {
        setRows(data.rows);
        setIsOverride(!!data.is_override);
      } else {
        // Pre-populate with frontend defaults so the admin can see what
        // the page is currently showing and curate from there.
        setRows(buildDefaultPricingForHotel(slug));
        setIsOverride(false);
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (idx, patch) =>
    setRows((cur) => cur.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => setRows((cur) => cur.filter((_, i) => i !== idx));
  const addRow = () => setRows((cur) => [...cur, blankPriceRow()]);
  const moveRow = (idx, dir) => setRows((cur) => {
    const next = [...cur];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return cur;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const updatePrice = (idx, key, val) => updateRow(idx, {
    prices: {
      ...rows[idx].prices,
      [key]: val === "" || val === null ? null : Number(val),
    },
  });

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/admin/pricing/${slug}`, {
      credentials: "include",
        method: "PUT",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ currency_base: "EUR", rows }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows);
      setIsOverride(!!data.is_override);
      setStatus({ type: "ok", msg: "Saved." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setResetting(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/admin/pricing/${slug}`, {
      credentials: "include",
        method: "DELETE",
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(buildDefaultPricingForHotel(slug));
      setIsOverride(false);
      setStatus({ type: "ok", msg: "Reset to defaults." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Reset failed" });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#5C5248] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading rates…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pricing-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif-x text-2xl tracking-tight">{hotel?.name}</h2>
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248] mt-1">
            {isOverride ? `Custom rates · ${rows.length} rows` : `Showing seeded defaults · ${rows.length} rows`}
            {" · EUR base"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={addRow} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-[#C16542] border border-[#C16542]/40 hover:bg-[#C16542] hover:text-white transition-colors" data-testid="pricing-add">
            <Plus className="w-3.5 h-3.5" /> Add row
          </button>
          <button type="button" onClick={resetToDefaults} disabled={resetting || !isOverride} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-[#5C5248] border border-[#2C2621]/15 hover:border-[#2C2621]/40 disabled:opacity-40" data-testid="pricing-reset">
            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reset
          </button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-white bg-[#C16542] hover:bg-[#a3502f] disabled:opacity-50" data-testid="pricing-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      </div>

      {status ? (
        <div className={`flex items-center gap-2 px-3 py-2 text-sm border ${status.type === "ok" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200"}`} data-testid="pricing-status">
          {status.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {status.msg}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="text-sm text-[#5C5248] italic">No rows — click <strong>Add row</strong> to start.</p>
      ) : (
        <ul className="space-y-4">
          {rows.map((r, idx) => (
            <li key={r.id || `idx-${idx}`} className="border border-[#2C2621]/12 bg-white p-5 space-y-4" data-testid={`pricing-row-${idx}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
                  Row {idx + 1}{r.room_id ? ` · ${r.room_id}` : ""}
                </p>
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => moveRow(idx, -1)} disabled={idx === 0} className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-30" aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => moveRow(idx, 1)} disabled={idx === rows.length - 1} className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-30" aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => removeRow(idx)} className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:border-red-500 hover:text-red-500" aria-label="Delete row"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <I18nField label="Room name" value={r.room_name} onChange={(v) => updateRow(idx, { room_name: v })} rows={1} testid={`pricing-row-${idx}-name`} />
              <I18nField label="Capacity" value={r.capacity} onChange={(v) => updateRow(idx, { capacity: v })} rows={1} testid={`pricing-row-${idx}-capacity`} />
              <I18nField label="Characteristics" value={r.characteristics} onChange={(v) => updateRow(idx, { characteristics: v })} rows={2} testid={`pricing-row-${idx}-char`} />

              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Prices (EUR · per room · per night)</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.keys(BOARD_BASIS_LABELS).map((k) => (
                    <label key={k} className="block">
                      <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mb-1 block">
                        {BOARD_BASIS_LABELS[k].en}
                      </span>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5248] text-sm pointer-events-none">€</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          value={r.prices?.[k] ?? ""}
                          onChange={(e) => updatePrice(idx, k, e.target.value)}
                          placeholder="—"
                          className="w-full border border-[#2C2621]/15 pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
                          data-testid={`pricing-row-${idx}-${k}`}
                        />
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminPricingInner = () => {
  const properties = useMemo(() => HOTELS.filter((h) => !h.coming_soon), []);
  const [activeSlug, setActiveSlug] = useState(properties[0]?.slug || "");
  const activeHotel = properties.find((h) => h.slug === activeSlug);

  return (
    <div className="min-h-screen bg-[#FBF6EE] text-[#2C2621]" data-testid="admin-pricing-page">
      <header className="bg-white border-b border-[#2C2621]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]"><ArrowLeft className="w-4 h-4" /> Admin</Link>
          <h1 className="font-serif-x text-2xl">Pricing</h1>
          <span className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">{properties.length} properties</span>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-12 gap-8">
        <aside className="md:col-span-4 lg:col-span-3">
          <nav className="bg-white border border-[#2C2621]/10 divide-y divide-[#2C2621]/10" data-testid="admin-pricing-property-list">
            {properties.map((h) => {
              const isOn = h.slug === activeSlug;
              return (
                <button type="button" key={h.slug} onClick={() => setActiveSlug(h.slug)} className={`w-full text-left px-4 py-3 text-sm transition-colors ${isOn ? "bg-[#C16542] text-white" : "hover:bg-[#FBF6EE] text-[#2C2621]"}`} data-testid={`admin-pricing-property-${h.slug}`}>
                  <div className="font-medium leading-tight">{h.name}</div>
                  <div className={`text-[10px] tracking-[0.22em] uppercase mt-1 ${isOn ? "text-white/80" : "text-[#5C5248]"}`}>{h.location}</div>
                </button>
              );
            })}
          </nav>
        </aside>
        <section className="md:col-span-8 lg:col-span-9">
          {activeSlug ? (
            <PricingEditor key={activeSlug} slug={activeSlug} hotel={activeHotel} />
          ) : (
            <p className="text-sm text-[#5C5248]">Select a property on the left.</p>
          )}
        </section>
      </div>
    </div>
  );
};

const AdminPricingPage = () => (
  <AdminAuthGate><AdminPricingInner /></AdminAuthGate>
);

export default AdminPricingPage;
